import { useCallback, useRef, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { FrameSequenceCanvas } from "@/components/FrameSequenceCanvas";
import { LanguageToggle } from "@/components/LanguageToggle";
import { Hero } from "@/components/sections/Hero";
import { TransitionSection } from "@/components/sections/TransitionSection";
import { TakeKrishnaHome } from "@/components/sections/TakeKrishnaHome";
import { FourPaths } from "@/components/sections/FourPaths";
import { InterestButton } from "@/components/journey/InterestCTA";
import { JourneyTabs } from "@/components/journey/JourneyTabs";
import { AudioTrack } from "@/components/AudioTrack";
import { SoundToggle } from "@/components/SoundToggle";
import { IntroLoader } from "@/components/IntroLoader";
import { useLanguage } from "@/context/LanguageContext";
import { fetchMicrosite, fetchHome, trackEvent, campaignAudioSrc } from "@/api/client";

// Default ambience for the home / default experience and any campaign that
// hasn't set its own track.
const DEFAULT_AUDIO = "/audio/website-ambience.mp3";

// Story beats mapped to scroll progress over the frame sequence.
const BEATS = [
  { at: 0.09, win: 0.1 }, // 1 Yashoda (frames 1-30)
  { at: 0.28, win: 0.07 }, // 2 Krishna appears (31-45)
  { at: 0.4, win: 0.07 }, // 3 Vasudeva carries (46-58)
  { at: 0.53, win: 0.09 }, // 4 Crossing Yamuna (59-78)
  { at: 0.82, win: 0.14 }, // 5 Gokul (79-128)
];

const beatOpacity = (progress, at, win) => {
  const d = progress - at;
  if (d < -win || d > win) return 0;
  // Text snaps to full opacity as the scene arrives and holds until its focal
  // point (no slow fade-in), then fades out only — a longer, easier-to-read
  // window than a symmetric fade in/out.
  if (d <= 0) return 1;
  return Math.pow(1 - d / win, 1.2);
};

export default function Landing({ presetCampaign = null }) {
  const { t, lang, setLang } = useLanguage();
  const { slug } = useParams();
  const [campaign, setCampaign] = useState(presetCampaign);
  const [entered, setEntered] = useState(false);
  const [progress, setProgress] = useState(0);
  const [showOptions, setShowOptions] = useState(false);
  const [audioOn, setAudioOn] = useState(false);
  const [headerCTA, setHeaderCTA] = useState({ anyRevealed: false, options: [], active: null, setActive: () => {} });
  const [headerHidden, setHeaderHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const lastY = useRef(0);
  const tabsActiveRef = useRef(false);
  const storyRef = useRef(null);
  const handleProgress = useCallback((p) => setProgress(p), []);

  // Once the journey tabs are active they live in the header and must stay
  // ALWAYS visible; before that, auto-hide the header on scroll-down / reveal
  // on scroll-up (removes the gap / jitter above the pathway tabs).
  useEffect(() => {
    tabsActiveRef.current = headerCTA.anyRevealed;
    if (headerCTA.anyRevealed) setHeaderHidden(false);
  }, [headerCTA.anyRevealed]);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY;
      setScrolled(y > 40);
      if (tabsActiveRef.current) {
        setHeaderHidden(false);
      } else if (y < 80) {
        setHeaderHidden(false);
      } else if (y > lastY.current + 6) {
        setHeaderHidden(true);
      } else if (y < lastY.current - 6) {
        setHeaderHidden(false);
      }
      lastY.current = y;
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Strangler pattern: /:slug loads a temple campaign; the homepage ("/") loads
  // the campaign flagged as "home" (so admin edits reflect on the main site).
  // Any failure falls back silently to the default hardcoded experience.
  useEffect(() => {
    if (presetCampaign) {
      setCampaign(presetCampaign);
      if (presetCampaign.slug) trackEvent(presetCampaign.slug, "website_visit");
      return;
    }
    let alive = true;
    if (slug) {
      fetchMicrosite(slug)
        .then((data) => {
          if (!alive) return;
          setCampaign(data);
          trackEvent(slug, "website_visit");
        })
        .catch(() => {});
    } else {
      fetchHome()
        .then((data) => alive && setCampaign(data))
        .catch(() => {}); // no home campaign configured → default experience
    }
    return () => {
      alive = false;
    };
  }, [slug, presetCampaign]);

  const startScrolling = () => {
    storyRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // The intro language tap doubles as the user gesture that unlocks audio.
  const handleEnter = (chosenLang) => {
    if (chosenLang) setLang(chosenLang);
    setAudioOn(true);
    setEntered(true);
    window.scrollTo({ top: 0 });
  };

  const handleShowMeHow = () => {
    setShowOptions(true);
    if (slug) trackEvent(slug, "journey_started");
    setTimeout(
      () =>
        document
          .getElementById("paths-title")
          ?.scrollIntoView({ behavior: "smooth", block: "start" }),
      160
    );
  };

  const brandName =
    (campaign?.is_temple && campaign?.temple_name) ||
    (campaign?.branding &&
      (lang === "hi"
        ? campaign.branding.display_name_hi
        : campaign.branding.display_name_en)) ||
    t.nav.brand;

  return (
    <div
      className={`relative bg-[#070b18] text-white antialiased ${
        lang === "hi" ? "lang-hi" : ""
      }`}
    >
      {!entered && <IntroLoader onEnter={handleEnter} />}
      {/* fixed chrome — auto-hides on scroll down, reveals on scroll up */}
      <header
        className={`fixed inset-x-0 top-0 z-50 flex items-center justify-between gap-4 px-5 py-4 transition-all duration-300 md:px-10 md:py-5 ${
          headerHidden ? "-translate-y-full" : "translate-y-0"
        } ${scrolled || headerCTA.anyRevealed ? "bg-[#070b18]/85 backdrop-blur-md" : ""}`}
      >
        <div className="flex shrink-0 items-center gap-2">
          <span className="text-xl">🪈</span>
          <span
            data-testid="brand-name"
            className="font-display text-base tracking-[0.15em] text-amber-100 md:text-lg"
          >
            {brandName}
          </span>
        </div>
        {headerCTA.anyRevealed && (
          <JourneyTabs
            variant="inline"
            options={headerCTA.options}
            active={headerCTA.active}
            onSelect={headerCTA.setActive}
            className="hidden flex-1 justify-center sm:flex"
          />
        )}
        <div className="flex shrink-0 items-center gap-2">
          {headerCTA.anyRevealed && <InterestButton options={headerCTA.options} />}
          <SoundToggle on={audioOn} toggle={() => setAudioOn((v) => !v)} />
          <LanguageToggle />
        </div>
      </header>

      <Hero onStart={startScrolling} />

      {/* Scroll-driven story */}
      <section
        id="story"
        ref={storyRef}
        data-testid="story-section"
        className="relative h-[calc(var(--vph)*7)]"
      >
        <div className="sticky top-0 h-[var(--vph)] w-full overflow-hidden">
          <FrameSequenceCanvas
            onProgress={handleProgress}
            sectionSelector="#story"
          />

          {/* legibility scrims */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#070b18] via-transparent to-[#070b18]/50" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-[#070b18]/70 via-transparent to-transparent" />

          {/* story beats */}
          <div className="pointer-events-none absolute inset-0 px-6 sm:px-12 lg:px-24">
            <div className="relative h-full">
              {BEATS.map((b, i) => {
                const o = beatOpacity(progress, b.at, b.win);
                const s = t.stories[i];
                return (
                  <div
                    key={i}
                    data-testid={`story-beat-${i}`}
                    className="absolute bottom-16 left-0 right-0 max-w-xl sm:bottom-auto sm:top-1/2 sm:right-auto sm:-translate-y-1/2"
                    style={{ visibility: o < 0.02 ? "hidden" : "visible" }}
                  >
                    <div
                      style={{
                        opacity: o,
                        transform: `translateY(${(1 - o) * 26}px)`,
                      }}
                    >
                      <p className="mb-3 text-xs uppercase tracking-[0.35em] text-amber-300/90 sm:text-sm">
                        {s.kicker}
                      </p>
                      <h2 className="whitespace-pre-line font-display text-4xl leading-[1.05] text-white sm:text-5xl lg:text-6xl">
                        {s.title}
                      </h2>
                      <p className="mt-4 max-w-md text-sm text-amber-50/70 sm:text-base md:text-lg">
                        {s.body}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* progress rail */}
          <div className="absolute right-5 top-1/2 hidden -translate-y-1/2 md:block">
            <div className="h-40 w-[2px] overflow-hidden rounded-full bg-white/10">
              <div
                data-testid="story-progress"
                className="w-full bg-gradient-to-b from-amber-300 to-amber-500"
                style={{ height: `${progress * 100}%` }}
              />
            </div>
          </div>
        </div>
      </section>

      <TransitionSection />
      <TakeKrishnaHome onShowMeHow={handleShowMeHow} />
      {showOptions && <FourPaths campaign={campaign} onJourneyState={setHeaderCTA} />}

      <AudioTrack src={campaignAudioSrc(campaign) || DEFAULT_AUDIO} enabled={audioOn} />
    </div>
  );
}
